//
// Based on:
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
//
// useIntervalAtMost
//
// Repeat "call" at "delayMs" intervals, except if call takes longer than that;
//   if longer, wait for call to finish before launching again.
//
// Usage:
//
// useIntervalAtMost(
//   call,
//   (enabled) ? delayMs : undefined,
// );
//
// Where call is of this type:
//
// const call = (callback) => {
//   // Do stuff...
//
//   // Signal we're done
//   callback();
// }
//

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const useIntervalAtMost = (call, delayMs, logLabel = undefined) => {
  const savedCall = useRef();
  const [paused, setPaused] = useState(false);
  const [running, setRunning] = useState(false);
  // For info only:
  const [calls, setCalls] = useState(0);

  // Erase savedCall when this module gets deleted
  useEffect(
    () => {
      if (logLabel) console.log(`useIntervalAtMost creating "${logLabel}"`);

      return () => {
        if (logLabel) console.log(`useIntervalAtMost deleting "${logLabel}"`);
        savedCall.current = undefined;
      };
    },
    [logLabel],
  );

  // Remember the latest callback.
  useEffect(
    () => {
      if (logLabel) console.log(`useIntervalAtMost call changed "${logLabel}"`);
      savedCall.current = call;
    },
    [logLabel, call],
  );

  useEffect(
    () => {
      if (logLabel) {
        console.log(`useIntervalAtMost "${logLabel}" - calls=${calls}, running=${
          running}, paused=${paused}, delayMs=${delayMs}`);
      }
    },
    [logLabel, delayMs, calls, running, paused],
  );

  const callbackEnded = useCallback(
    () => { setRunning(false); },
    [],
  );

  useEffect(
    () => {
      if ((!paused) && (!running) && delayMs) {
        setTimeout(
          () => { if (savedCall.current) setPaused(false); },
          delayMs,
        );
        setPaused(true);
        setRunning(true);
        savedCall.current(callbackEnded);

        // Keep track of calls for logging:
        setCalls((prev) => (prev + 1));
      }
    },
    [callbackEnded, paused, running, delayMs],
  );
};

export default useIntervalAtMost;
